import { useMutation, useQueryClient } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  toDivisionPayload,
  type DivisionFormValues,
} from '@sportninja/common/features/competition/operations/utils/toDivisionPayload'
import type { OperationsScheduleNode } from '@sportninja/common/features/competition/operations/types/operations.types'

interface Args {
  orgId: string
  rootScheduleId: string
  /** Canlan-only registration/Salesforce fields, gated exactly as in legacy. */
  showSalesforceSettings?: boolean
}

/**
 * Creates a child competition (division / conference / season) under an
 * existing schedule — `POST /organizations/:orgId/schedules` with `parent_id`,
 * the same call the legacy Setup tab makes.
 *
 * A plain `POST /schedules` would create a top-level competition instead, which
 * is the trap this endpoint choice avoids.
 */
export const useCreateDivision = ({
  orgId,
  rootScheduleId,
  showSalesforceSettings = false,
}: Args) => {
  'use memo'

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      values,
      parent,
      isPublic,
    }: {
      values: DivisionFormValues
      parent: OperationsScheduleNode
      isPublic: boolean
    }) => {
      const body = toDivisionPayload({
        values,
        parent: {
          scheduleId: parent.scheduleId,
          startsAt: parent.startsAt,
          endsAt: parent.endsAt,
        },
        parentId: parent.scheduleId,
        isPublic,
        showSalesforceSettings,
      })

      return req(`/organizations/${orgId}/schedules`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    // No onError toast: DivisionModal catches the rejection and decides between
    // inline field errors and a single toast. Toasting here too would double up
    // on every failure, and would fire alongside the inline messages that are
    // meant to be the only surface for field errors.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.operations.tree(rootScheduleId),
      })
    },
  })

  return {
    createDivision: mutation.mutateAsync,
    isCreating: mutation.isPending,
  }
}

/**
 * Edits an existing child competition — `PUT /schedules/:id`, the same call the
 * legacy Setup tab makes.
 *
 * Same payload builder as create, minus `parent_id` and `is_public`: this PUTs
 * to the node's own URL, so resending those would risk re-parenting it or
 * overwriting its visibility.
 */
export const useUpdateDivision = ({
  rootScheduleId,
  showSalesforceSettings = false,
}: Omit<Args, 'orgId'>) => {
  'use memo'

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      values,
      node,
    }: {
      values: DivisionFormValues
      node: OperationsScheduleNode
    }) => {
      const body = toDivisionPayload({
        values,
        // On edit the dates are the node's own, not a parent's.
        parent: {
          scheduleId: node.scheduleId,
          startsAt: node.startsAt,
          endsAt: node.endsAt,
        },
        showSalesforceSettings,
      })

      return req(`/schedules/${node.scheduleId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
    },
    // No onError toast: DivisionModal catches the rejection and decides between
    // inline field errors and a single toast. Toasting here too would double up
    // on every failure, and would fire alongside the inline messages that are
    // meant to be the only surface for field errors.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.operations.tree(rootScheduleId),
      })
    },
  })

  return {
    updateDivision: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  }
}
